import { DB_CONFIG_URL } from '@core/settings/settings.constants';
import { ApplicationConfig, mergeApplicationConfig, Type } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { PromiseLoad } from '@core/auth/promise-load';
import { getTestBed } from '@angular/core/testing';
import { BrowserDynamicTestingModule, platformBrowserDynamicTesting } from '@angular/platform-browser-dynamic/testing';
import { StorageKeys } from '@core/storage/storage.keys';
import { set } from 'lodash-es';
import { SettingsDBModel, SettingsDBResponse } from '@core/settings';


export interface CustomAngularApp {
    bootstrapApp(root?: Type<unknown>, appConfig?: ApplicationConfig): void;
}

export class CreateBoApp<T> {
    readonly #databaseSettingsConfigUrl: string = DB_CONFIG_URL;
    readonly #root: T;
    readonly #appConfig: ApplicationConfig;
    readonly #isTesting: boolean;
    #app: CustomAngularApp;

    readonly #databaseSettingsConfigRequest: Promise<SettingsDBResponse> = new PromiseLoad(
        this.#databaseSettingsConfigUrl,
        StorageKeys.Access,
    ).get<SettingsDBResponse>();

    constructor(root: T, appConfig: ApplicationConfig, isTesting?: boolean) {
        this.#root = root;
        this.#appConfig = appConfig;
        this.#isTesting = isTesting;
    }

    private bootstrap(): void {
        if (this.#isTesting) {
            this.#app = new AngularBoTestAppWithSettings();
            this.#app.bootstrapApp();
            new AngularBoTestAppWithSettings();
        } else {
            this.#app = new AngularBoAppWithSettings();
            this.#app.bootstrapApp(this.#root as Type<unknown>, this.#appConfig);
        }
    };

    private provideSettingsInWindow(settings: SettingsDBResponse): void {
        if (!window) {
            return;
        }

        set(window, 'appSettings.phoneCode', settings?.find(
            (item: SettingsDBModel) => item.name === 'DEFAULT_PHONE_COUNTRY',
        )?.value);

        set(window, 'appSettings.currencyFormat', settings?.find(
            (item: SettingsDBModel) => item.name === 'CURRENCY_FORMAT',
        )?.value);

        set(window, 'appSettings.enableParking', settings?.find(
            (item: SettingsDBModel) => item.name === 'PARKING_ENABLED',
        )?.value);
    }

    public initApp(): void {
        this.#databaseSettingsConfigRequest.then(
            (response: SettingsDBResponse): void => {
                this.provideSettingsInWindow(response);
                this.bootstrap();
            },
            (): void => {
                this.bootstrap();
            },
        );
    }
}

export class AngularBoAppWithSettings implements CustomAngularApp {
    bootstrapApp(root: Type<unknown>, appConfig: ApplicationConfig): void {
        bootstrapApplication(
            root,
            mergeApplicationConfig(appConfig),
        ).catch((err) => console.error(err));
    }
}

export class AngularBoTestAppWithSettings implements CustomAngularApp {
    bootstrapApp() {
        getTestBed().initTestEnvironment(
            BrowserDynamicTestingModule,
            platformBrowserDynamicTesting(),
            {
                teardown: { destroyAfterEach: false },
            },
        );
    }
}
